/**
 * field.tier_breakdown — relationship tier histogram for the workspace.
 * Pulls members-index, buckets by relationship_tier (hot/warm/cool/cold),
 * renders as horizontal bars with counts. Quick visual snapshot of the
 * field operation's coverage.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { WidgetProps } from "../registry";

const TIER_ORDER = ["hot", "warm", "cool", "cold", "never_engaged"] as const;
const TIER_BG: Record<string, string> = {
  hot: "bg-red-500",
  warm: "bg-orange-500",
  cool: "bg-sky-500",
  cold: "bg-stone-400",
  never_engaged: "bg-stone-200",
};
const TIER_LABEL: Record<string, string> = {
  hot: "Hot",
  warm: "Warm",
  cool: "Cool",
  cold: "Cold",
  never_engaged: "Never engaged",
};

export function FieldTierBreakdown({ workspaceId }: WidgetProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "field.tier_breakdown", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/members-index`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Relationship tiers could not be loaded." retry={refetch} />;
  if (isLoading) return <div role="status" className="space-y-1.5"><span className="sr-only">Loading…</span><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-3/4" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-1/2" /><span aria-hidden="true" className="cmcp-skeleton h-3.5 w-2/3" /></div>;
  const members: any[] = data?.members ?? [];
  if (members.length === 0) return <div className="text-stone-600 text-xs">No members yet.</div>;

  const counts: Record<string, number> = {};
  for (const m of members) {
    const t = m.relationship_tier || "never_engaged";
    counts[t] = (counts[t] ?? 0) + 1;
  }
  const total = members.length;
  const max = Math.max(...TIER_ORDER.map((t) => counts[t] ?? 0));

  return (
    <div className="space-y-1.5 text-xs">
      <div className="text-stone-600 text-[11px] mb-1">{total} total members</div>
      {TIER_ORDER.map((tier) => {
        const c = counts[tier] ?? 0;
        const pct = max > 0 ? (c / max) * 100 : 0;
        return (
          <div key={tier} className="flex items-center gap-2">
            <div className="w-24 shrink-0 text-stone-700 text-[11px]">{TIER_LABEL[tier]}</div>
            <div className="flex-1 h-4 bg-stone-100 rounded overflow-hidden">
              <div className={`h-full ${TIER_BG[tier]}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="w-8 text-right font-mono text-stone-700 tabular-nums">{c}</div>
          </div>
        );
      })}
    </div>
  );
}
